import { Add } from '@mui/icons-material'
import { Button, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import InCardStack from 'components/atoms/container/InCardStack'
import UserConnectionForm from 'components/atoms/input/UserConnectionForm'
import { ActionText } from 'components/atoms/Text'
import rocketAddIcon from 'assets/images/rocketAdd.svg'
import sendEmailPaperPlaneIcon from 'assets/images/sendEmailPaperPlane.svg'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MY_JOURNEY } from 'routes/constants'
import {
  ASSESSMENT_RECIPIENTS_URL,
  ASSESSMENT_URL,
  INVITED_LIST_URL
} from 'services/constants'
import { getRequest, postRequest } from 'utils/api'
import { getUserConnections } from 'services/userServices'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { useTranslation } from 'react-i18next'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'
import InfoDialog from 'components/molecules/notificationOverlay/InfoDialog'
import AvatarChip from 'components/atoms/AvatarChip'

const AddRatersCard = ({
  fromSelfAssessment,
  goalId,
  data,
  completeCallback,
  buttonDirection = 'column',
  inPopup = false
}) => {
  const { t } = useTranslation()
  const [assessmentData, setAssessmentData] = useState(data)
  const [existingRaters, setExistingRaters] = useState(
    assessmentData?.raters || []
  )
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(fromSelfAssessment || false)
  const [recipients, setRecipients] = useState([])
  const [raters, setRaters] = useState([])
  const [requestSucess, setRequestSucess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [displayError, setDisplayError] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      let fetchedRaters = existingRaters
      let previousRaters = assessmentData?.previousRaters

      if (!assessmentData) {
        const assessmentRes = await getRequest(ASSESSMENT_URL, {
          goalId
        })
        if (assessmentRes.status === 200) {
          setAssessmentData(assessmentRes.data.data)
          fetchedRaters = assessmentRes.data.data.raters
          setExistingRaters(fetchedRaters)
          previousRaters = assessmentRes.data.data.previousRaters
        }
      }
      let connectionList = []
      const res = await getUserConnections()
      const resInvites = await getRequest(INVITED_LIST_URL, {
        profileOnly: true
      })
      if (res.length && resInvites.status === 200) {
        const connectedEmails = res
          .filter(r => r.id && !r.userId)
          .map(u => u.user.email)
        const invited = resInvites.data.data
          .filter(i => connectedEmails.indexOf(i.email) < 0)
          .map(i => ({ user: i, ...i }))

        const completeList = res.concat(invited)
        if (fetchedRaters > 0) {
          const selectedRaters = fetchedRaters.map(r => r.email)
          connectionList = completeList.filter(
            r => selectedRaters.indexOf(r.email) < 0
          )
        } else {
          connectionList = completeList
        }
      }

      // if assessment does not have any raters and has previous raters, populate raters
      if (previousRaters?.length > 0 && fetchedRaters?.length === 0) {
        previousRaters = previousRaters.map(r => {
          const foundConnection = connectionList.find(l => l.email === r.email)
          return foundConnection || r
        })
        if (previousRaters.length < 5) {
          previousRaters.push(...Array(5 - previousRaters.length).fill(null))
        }
      }
      setRecipients(connectionList)
      if (previousRaters?.length > 0 && fetchedRaters?.length === 0) {
        setRaters(previousRaters)
      } else {
        setRaters(Array(inPopup ? 3 : 5).fill(null))
      }
      setLoading(false)
    }
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRaterChange = (index, rater) => {
    const newRaters = [...raters]
    newRaters[index] = rater
    setRaters(newRaters)
    checkSubmittable(newRaters)
  }

  const addRaters = () => {
    const newRaters = [...raters]
    const emptyRaters = Array(3).fill(null)
    newRaters.push(...emptyRaters)
    setRaters(newRaters)
  }

  const submitRaters = async () => {
    const validData = checkSubmittable(raters)
    if (!validData) {
      setDisplayError(true)
      return
    }
    setLoading(true)
    const invitees = raters
      .map(r => {
        if (r?.email) {
          return {
            email: r.email,
            firstName: r.firstName ?? null,
            lastName: r.lastName ?? null,
            relationship: r.relationship
          }
        } else {
          return null
        }
      })
      .filter(r => r !== null)
    const requestBody = {
      goalId,
      invitees
    }

    const res = await postRequest(ASSESSMENT_RECIPIENTS_URL, requestBody)
    if (res.status === 200) {
      setRequestSucess(true)
    } else {
      setDisplayError(true)
      setErrorMessage(t('requestRetryAlert.message'))
    }
    setLoading(false)
  }

  const checkSubmittable = checkRaters => {
    const filtered = checkRaters.filter(r => r !== null && !r.error)
    const errorInput = checkRaters.find(r => r?.error)
    const missingRelationship = checkRaters.find(r => r && !r.relationship)

    if (filtered.length < 3 && existingRaters.length === 0) {
      setErrorMessage(t('addRaters.minimum3ConnValidation'))
      return false
    } else if (filtered.length === 0 || missingRelationship) {
      // When there are existing raters, allow sending <3 and >0 new raters
      setErrorMessage(t('addRaters.validConnError'))
      return false
    } else if (errorInput) {
      setErrorMessage(errorInput.errorMessage)
      return false
    } else {
      setErrorMessage('')
      return true
    }
  }

  const handleSelfAssessmentFinish = () => {
    navigate(MY_JOURNEY)
  }
  return (
    <>
      <OverlayLoading open={loading} />
      {existingRaters?.length ? (
        <>
          <Typography variant='h4'>
            {t('addRaters.invitedRaters', { count: existingRaters.length })}
          </Typography>
          <Stack padding={0} spacing={2}>
            {existingRaters.map(r => {
              return (
                <AvatarChip
                  key={r.email}
                  user={r}
                  chipNameWithEmail={true}
                  variant='filled'
                  sx={{ width: 'fit-content' }}
                  hideProfileImage={true}
                />
              )
            })}
          </Stack>
        </>
      ) : null}
      <InCardStack width={700} spacing={4}>
        {raters.map((r, i) => (
          <UserConnectionForm
            key={i}
            index={i}
            connections={recipients}
            selected={raters}
            submitHandler={handleRaterChange}
            existingRaters={existingRaters}
          />
        ))}
        <ActionText
          display='flex'
          alignItems='center'
          width='100%'
          paddingTop={5}
          paddingLeft={10}
          variant='h5'
          onClick={() => addRaters()}>
          <Add color='primary' /> {t('addRaters.addMoreRaters')}
        </ActionText>
      </InCardStack>
      <Grid
        container
        direction={buttonDirection}
        display='flex'
        alignContent='center'
        justifyContent='center'
        spacing={12}>
        <Grid order={buttonDirection === 'column' ? 1 : 2}>
          <Button onClick={() => submitRaters()}>
            {t('addRaters.sendInvites')}
          </Button>
        </Grid>
        <Grid order={buttonDirection === 'column' ? 2 : 1}>
          {buttonDirection === 'column' ? (
            <Button
              color='muted'
              onClick={() => handleSelfAssessmentFinish()}>
              {t('addRaters.skipForNow')}
            </Button>
          ) : (
            <Button
              color='muted'
              onClick={() =>
                completeCallback ? completeCallback() : navigate(-1)
              }>
              {t('common.cancel')}
            </Button>
          )}
        </Grid>
      </Grid>
      <InfoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t('addRaters.getMostFromResults')}
        buttonText={t('addRaters.getStarted')}
        icon={rocketAddIcon}>
        <Typography>{t('addRaters.nextStepToGetOthersRate')}</Typography>
        <Typography fontWeight='bold'>
          {t('addRaters.importantToNote')}
        </Typography>
        <Typography>{t('addRaters.rateAndCompare')}</Typography>
      </InfoDialog>
      <InfoDialog
        open={requestSucess}
        onClose={() => (inPopup ? completeCallback() : navigate(MY_JOURNEY))}
        title={t('addRaters.invitesSent')}
        buttonText={inPopup ? t('common.done') : t('common.okGoMyJourney')}
        icon={sendEmailPaperPlaneIcon}>
        <Typography variant='body1Large'>
          {t('addRaters.emailSuccessfullySent')}
        </Typography>
      </InfoDialog>
      <NotificationSnackBar
        open={displayError}
        setOpen={setDisplayError}
        error={true}
        errorText={errorMessage}
        autoHideDuration={3000}
      />
    </>
  )
}

export default AddRatersCard
